import { useState } from 'react';
import {
	Box,
	Typography,
	TextField,
	Paper,
	Stack,
	Alert,
	Button,
	InputAdornment,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Actions from '../../components/Actions';
import { Navigate, useOutletContext, useRevalidator } from 'react-router';
import HandymanIcon from '@mui/icons-material/Handyman';
import CheckIcon from '@mui/icons-material/Check';
import CircleIcon from '@mui/icons-material/Circle';
import { authedApiFetch } from '../../utils/Api';

const TenantAdmin = () => {
	// React Router hooks
	const { tenant, user } = useOutletContext();
	const revalidator = useRevalidator();

	const [salesforceClientId, setSalesforceClientId] = useState(
		tenant.salesforce_eca_client_id || ''
	);
	const [salesforceClientSecret, setSalesforceClientSecret] = useState(
		tenant.salesforce_eca_client_secret || ''
	);
	const [salesforceOrgDomain, setSalesforceOrgDomain] = useState(
		(tenant.salesforce_org_url || '').replace(/^https?:\/\//, '')
	);
	const [setupLoading, setSetupLoading] = useState(false);
	const [setupError, setSetupError] = useState(null);
	const [isEditing, setIsEditing] = useState(false);

	const handleSetupSalesforceEca = async (e) => {
		e.preventDefault();
		setSetupLoading(true);
		setSetupError(null);

		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/setup_salesforce_eca`,
				method: 'POST',
				payload: {
					salesforce_eca_client_id: salesforceClientId,
					salesforce_eca_client_secret: salesforceClientSecret,
					salesforce_org_url: salesforceOrgDomain
						? `https://${salesforceOrgDomain}`
						: '',
				},
			});
			revalidator.revalidate();
			setIsEditing(false);
		} catch (err) {
			setSetupError(err.message);
		} finally {
			setSetupLoading(false);
		}
	};

	// handler passed to action component, called after confirming action
	const handleCreateUserPool = async () => {
		await authedApiFetch({
			endpoint: `/tenants/${tenant.id}/user_pool`,
			method: 'POST',
		});

		// imperatively revalidate b/c not using React Router action.
		revalidator.revalidate();
	};

	// Array of actions passed to Action component
	const actions = [
		{
			name: 'createUserPool',
			label: 'Create User Pool and App Clients',
			icon: <HandymanIcon />,
			disabledIcon: <CheckIcon />,
			confirmText:
				'This will create a user pool and app clients for the current tenant.',
			disabledCheck: () => !!tenant.user_pool_id,
			actionHandler: handleCreateUserPool,
		},
	];

	// Route back to marketplace if non-admin user attempts to navigate directly
	if (user.role != 'altvia_admin' && user.role != 'altvia_power_user') {
		return <Navigate to={`/tenants/${tenant.id}/marketplace`} />;
	} else {
		return (
			<>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						rowGap: '16px',
						height: '100vh',
						paddingTop: '12px',
						overflowY: 'auto',
						pb: 4,
					}}
				>
					<Typography variant='h4'>Tenant Admin</Typography>
					<Actions actions={actions} />

					{user.role === 'altvia_admin' && (
						<Paper sx={{ p: 3, mt: 4, maxWidth: '800px' }}>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									mb: 2,
								}}
							>
								<Typography variant='h6' gutterBottom>
									Salesforce ECA Integration
								</Typography>
								<Button
									variant={isEditing ? 'contained' : 'outlined'}
									color='error'
									size='small'
									onClick={() => setIsEditing(!isEditing)}
									disabled={setupLoading}
								>
									{isEditing ? 'Cancel' : 'Edit'}
								</Button>
							</Box>
							<Typography variant='body2' color='text.secondary'>
								Register this Tenant&apos;s Salesforce External Client App
								credentials to enable user-level SSO to the connected Salesforce
								org. After creating an ECA in Salesforce, these credentials can
								be found in Salesforce Setup -&gt; External Client App Manager.
							</Typography>

							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'flex-start',
									my: 3,
									gap: 1,
								}}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<CircleIcon
										sx={{
											fontSize: 14,
											color: tenant.salesforce_eca_configured
												? 'success.main'
												: 'error.main',
										}}
									/>
									<Typography variant='body2' sx={{ fontWeight: 600 }}>
										Status:{' '}
										{tenant.salesforce_eca_configured
											? 'Configured'
											: 'Not Configured'}
									</Typography>
								</Box>
								{tenant.salesforce_eca_configured && (
									<Typography variant='caption' color='text.secondary'>
										Credentials have been registered with Composio. To verify
										the integration, go to the Connections feature and attempt
										to &apos;Connect&apos; a Salesforce account. Valid
										credentials will successfully redirect you to the Salesforce
										login page.
									</Typography>
								)}
								{tenant.salesforce_auth_config_id && (
									<Typography variant='caption' color='text.secondary'>
										Composio Auth Config ID: {tenant.salesforce_auth_config_id}
									</Typography>
								)}
							</Box>

							<form onSubmit={handleSetupSalesforceEca}>
								<Stack spacing={2}>
									<TextField
										label='Consumer Key'
										type='password'
										variant='outlined'
										size='small'
										sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
										value={salesforceClientId}
										onChange={(e) => setSalesforceClientId(e.target.value)}
										required
										disabled={setupLoading || !isEditing}
									/>
									<TextField
										label='Consumer Secret'
										type='password'
										variant='outlined'
										size='small'
										sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
										value={salesforceClientSecret}
										onChange={(e) => setSalesforceClientSecret(e.target.value)}
										required
										disabled={setupLoading || !isEditing}
									/>
									<TextField
										label='Salesforce Org URL'
										variant='outlined'
										size='small'
										placeholder='your-domain.my.salesforce.com'
										helperText='Found in Salesforce Setup → My Domain'
										value={salesforceOrgDomain}
										onChange={(e) => setSalesforceOrgDomain(e.target.value)}
										required
										disabled={setupLoading || !isEditing}
										slotProps={{
											input: {
												startAdornment: (
													<InputAdornment
														position='start'
														sx={{ marginRight: '0.1rem' }}
													>
														https://
													</InputAdornment>
												),
											},
										}}
									/>
									{setupError && (
										<Alert severity='error' size='small'>
											{setupError}
										</Alert>
									)}
									<Box sx={{ alignSelf: 'center' }}>
										<LoadingButton
											type='submit'
											variant='contained'
											loading={setupLoading}
											disabled={!isEditing}
										>
											{tenant.salesforce_eca_configured
												? 'Update Credentials'
												: 'Connect to Salesforce'}
										</LoadingButton>
									</Box>
								</Stack>
							</form>
						</Paper>
					)}
				</Box>
			</>
		);
	}
};

export default TenantAdmin;
